import React, { useState, useEffect, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { Flex } from 'ui/acro'
import { Feedback } from 'ui/bend'
import Text from 'ui/acro/typography/Text'
import OTPInput from 'react-otp-input'
import IncorrectOtpError from './IncorrectOtpError'
import { axios } from '../../../api'
import {
  CALLBACK_URL,
  HAS_ALO_AUTH_TOKEN_STATE_COOKIE,
  setCookie,
  redirect_to_with_cookie_creation
} from 'components_v2/common'
import {
  sendAccessTokenToApp,
  createKeyDownHandler,
  createPasteHandler
} from './utils'
import StyledLink from 'components_v2/CommonComponents/StyledLink'
import { sign_up_event } from 'gtm/pushEvents'
import { sign_up_braze_event } from 'braze/brazeEvents'

const Wrapper = styled(Flex)`
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 480px;
  margin: 0 auto;
  padding: 48px 16px;
  text-align: center;
`

const TitleText = styled.div`
  color: #000;
  font-family: 'Proxima-Nova';
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
  text-transform: uppercase;
  margin-bottom: 24px;
`

const DescriptionText = styled(Text)`
  color: #3b3b3b;
  font-family: 'Proxima-Nova';
  font-size: 18px;
  font-weight: 400;
  line-height: 28px;
  letter-spacing: 0.4px;
  margin-bottom: 32px;
  text-align: left;
`

const Link = styled(StyledLink)`
  margin-top: 20px;
  width: 100%;
`

const ExistingUserAloSignUp = ({
  email,
  setCustomerEmail,
  iosAppClientId,
  iosAppVersion,
  iosAppApiVersion,
  submitData = {},
  iosAppScope,
  isFromVerifyPage = false,
  isFromSignUpPage = false
}) => {
  const [otp, setOtp] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [maxLimit, setMaxLimit] = useState(false)
  const [showOtpError, setShowOtpError] = useState(false)
  const [resending, setResending] = useState(false)
  const [erroMessage, setErroMessage] = useState(
    'Incorrect Code. Please try again.'
  )
  const [isBlocked, setIsBlocked] = useState(false)
  const [blockedUntil, setBlockedUntil] = useState(null)
  const [disableSendAgainLink, setDisableSendAgainLink] = useState(false)

  const iosAppQueryParams = useMemo(
    () =>
      Boolean(iosAppClientId)
        ? {
            version: iosAppVersion,
            os: 'ios',
            api_version: iosAppApiVersion,
            client_id: iosAppClientId,
            scope: iosAppScope
          }
        : {},
    [iosAppClientId, iosAppApiVersion, iosAppScope, iosAppVersion]
  )

  useEffect(() => {
    const otpBlockedUntil = localStorage.getItem('otp_blocked_until')?.[email]

    if (otpBlockedUntil) {
      const time = parseInt(otpBlockedUntil, 10)

      if (Date.now() < time) {
        setBlockedUntil(time)
        setIsBlocked(true)
      } else {
        clearLocalStorage()
      }
    }
  }, [])

  useEffect(() => {
    if (!blockedUntil) return

    const remainingTime = blockedUntil - Date.now()

    if (remainingTime <= 0) {
      setIsBlocked(false)
      setBlockedUntil(null)
      clearLocalStorage()
      return
    }

    const timeout = setTimeout(() => {
      setIsBlocked(false)
      setBlockedUntil(null)
      clearLocalStorage()
    }, remainingTime)

    return () => clearTimeout(timeout)
  }, [blockedUntil])

  const clearLocalStorage = () => {
    const data = JSON.parse(localStorage.getItem('otp_blocked_until')) || {}
    delete data[email]

    localStorage.setItem('otp_blocked_until', JSON.stringify(data))
  }

  const handleKeyDown = createKeyDownHandler()
  const handlePaste = createPasteHandler({
    onPasteComplete: setOtp
  })

  const VerifyOtp = async () => {
    setIsSubmitting(true)
    try {
      const res = await axios.post('/sso/alo_link_with_otp', {
        ...submitData,
        otp,
        email,
        ...iosAppQueryParams,
        redirect_to: CALLBACK_URL
      })
      const {
        login = 'failed',
        redirectUrl = '/',
        error = '',
        linkedUser = false,
        user,
        userLinked = false,
        accessTokens
      } = res.data
      if (login === 'successful') {
        if (userLinked && user) {
          sign_up_event(user.userAloTier, true, user)
          sign_up_braze_event(user.userAloTier, true, user)
        }
        setCookie('alo_access_session_established', 'true', 1 / 24)
        sendAccessTokenToApp({
          email,
          accessTokens,
          linkedUser,
          user,
          isVerifyWebViewSuccess: isFromVerifyPage,
          isSignUpWebViewSuccess: isFromSignUpPage,
          isExistingAwcUser: true
        })
        if (!Boolean(iosAppClientId)) {
          redirect_to_with_cookie_creation(redirectUrl ? redirectUrl : '/')
        }
      } else {
        setErroMessage(error || 'Incorrect Code. Please try again.')
        setShowOtpError(true)
        sendAccessTokenToApp({
          email,
          error,
          isVerifyWebViewSuccess: false,
          isSignUpWebViewSuccess: false
        })
      }
    } catch (error) {
      setErroMessage(
        error?.response?.data?.error || 'Incorrect Code. Please try again.'
      )
      if (
        error?.response?.status === 401 &&
        error?.response?.data?.error ===
          'Maximum attempts exceeded. Please request a new OTP.'
      ) {
        setOtp('')
        setIsBlocked(true)
      }
      if (error?.response?.status === 403) {
        setOtp('')
        const blockTime = Date.now() + 15 * 60 * 1000 // 15 minutes
        setBlockedUntil(blockTime)
        setIsBlocked(true)

        localStorage.setItem(
          'otp_blocked_until',
          JSON.stringify({ [email]: blockTime })
        )
      }

      setShowOtpError(true)
      sendAccessTokenToApp({
        email,
        error,
        isVerifyWebViewSuccess: false,
        isSignUpWebViewSuccess: false
      })
    } finally {
      setTimeout(() => {
        setOtp('')
        setIsSubmitting(false)
      }, 1000)
    }
  }

  const handleVerity = useCallback(VerifyOtp, [
    email,
    otp,
    iosAppQueryParams,
    iosAppClientId
  ])

  useEffect(() => {
    if (otp.length === 6 && !isSubmitting) {
      handleVerity()
    }
  }, [otp, isSubmitting, handleVerity])

  const resendOtp = async () => {
    setMaxLimit(false)
    setResending(true)
    try {
      await axios
        .post('/sso/send_alo_link_otp', { email })
        .then((res) => {
          if (res?.data?.error) {
            setMaxLimit(res?.data?.message)
          }
          setIsBlocked(false)
        })
        .catch()
      setOtp('')
    } catch (error) {
      setOtp('')
      setErroMessage('Error resending OTP')
      setShowOtpError(true)
      console.error('Error resending OTP:', error)
    } finally {
      setResending(false)
    }
  }

  const handleSendAgain = () => {
    setDisableSendAgainLink(true)

    setTimeout(() => {
      setDisableSendAgainLink(false)
    }, 60000)

    resendOtp()
  }

  return (
    <Wrapper>
      <TitleText>ACCOUNT FOUND</TitleText>
      <DescriptionText>
        Looks like you already have an ALO Wellness Club account! Please enter
        the one-time passcode sent to {email} so that we can connect your
        accounts.
      </DescriptionText>

      <OTPInput
        value={otp}
        onChange={setOtp}
        numInputs={6}
        isInputNum={true}
        inputType='tel'
        inputMode='numeric'
        renderInput={(props) => (
          <input
            {...props}
            onKeyDown={handleKeyDown}
            disabled={isBlocked}
            onPaste={isBlocked ? (e) => e.preventDefault() : handlePaste}
            style={{
              width: '32px',
              height: '44px',
              fontSize: '18px',
              marginLeft: '15px',
              marginRight: '12px',
              textAlign: 'center',
              borderRadius: '4px',
              padding: '0px',
              border: '1px solid #ccc',
              backgroundColor: isBlocked ? '#eee' : 'white',
              cursor: isBlocked ? 'not-allowed' : 'text'
            }}
          />
        )}
      />
      {/* <VerifyBtn
        type='submit'
        disabled={otp.length < 6}
        processing={isSubmitting}
        onClick={VerifyOtp}
        id='alo_otp_verify_button'
      >
        Verify
      </VerifyBtn> */}

      {showOtpError && (
        <IncorrectOtpError
          message={erroMessage}
          show={showOtpError}
          onClose={() => setShowOtpError(false)}
        />
      )}

      {maxLimit ? (
        <Feedback
          className='error'
          style={{
            alignSelf: 'flex-start',
            marginTop: '24px',
            marginLeft: '0'
          }}
        >
          {maxLimit}
        </Feedback>
      ) : (
        <Link>
          <>
            Didn’t get an email?
            <span
              onClick={
                blockedUntil || disableSendAgainLink
                  ? undefined
                  : handleSendAgain
              }
              style={{
                cursor:
                  blockedUntil || disableSendAgainLink
                    ? 'not-allowed'
                    : 'pointer',
                color: blockedUntil || disableSendAgainLink ? '#aaa' : '',
                pointerEvents:
                  blockedUntil || disableSendAgainLink ? 'none' : 'auto'
              }}
            >
              {resending ? 'sending...' : 'Send again'}
            </span>
          </>
        </Link>
      )}

      <Link style={{ marginTop: '8px' }}>
        <span
          style={{ marginLeft: '0' }}
          onClick={() => {
            setCustomerEmail('')
          }}
        >
          Go Back
        </span>
      </Link>
    </Wrapper>
  )
}

export default ExistingUserAloSignUp
